import styled from "styled-components";
import CustomCalendarGrid from "../../../common/components/calendar/CustomCalendarGrid";
import CustomSubTab from "../../../common/components/tab/CustomSubTab";
import { Padding, Row, SingleScrollView, SizedBox } from "../../../common/components/CommonBaseComponents";
import CustomIcon from "../../../common/components/image/CustomIcon";
import { useEffect, useRef, useState } from "react";
import {
    format,
    subDays,
    subWeeks,
    subMonths,
    subYears,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfYear,
    endOfYear,
} from "date-fns";
import StoreService from "../../../service/StoreService";
import { C1Text, C3Text } from "../../../common/components/text/CustomText";
import { CustomMultiFilterButton } from "../../../common/components/button/CustomMultiFilterButton";
import CustomMonthSelectButton from "../../../common/components/button/CustomMonthSelectButton";
import CustomLineGraphChart from "../../../common/components/chart/CustomLineGraphChart";
import CustomPopup from "../../../common/components/popup/CustomPopup";
import Translate from "../../../common/translate/Translate";
import { Theme } from "../../../common/Theme";

const WebCalendarGrid = styled.div`
    display: grid;
    grid-template-columns: 1.2fr 0.8fr;
    gap: 17px;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
`;

const StatusValues = [
    { label: Translate("store.chart.allPeriod"), value: "allPeriod" },
    { label: Translate("store.chart.hourly"), value: "hourly" },
    { label: Translate("store.chart.daily"), value: "daily" },
    { label: Translate("store.chart.weekday"), value: "weekday" },
    { label: Translate("store.chart.weekly"), value: "weekly" },
    { label: Translate("store.chart.monthly"), value: "monthly" },
    { label: Translate("store.chart.yearly"), value: "yearly" },
];

// 차트 필터 value ↔ 매장별 수익 조회 API 의 periodType 매핑
const CHART_PERIODS = [
    { value: "hourly", periodType: "hour", titleKey: "store.chart.hourly" },
    { value: "daily", periodType: "day", titleKey: "store.chart.daily" },
    { value: "weekday", periodType: "dayofweek", titleKey: "store.chart.weekday" },
    { value: "weekly", periodType: "week", titleKey: "store.chart.weekly" },
    { value: "monthly", periodType: "month", titleKey: "store.chart.monthly" },
    { value: "yearly", periodType: "year", titleKey: "store.chart.yearly" },
];

// periodType 별 조회 기간 → { startDate, endDate } (yyyy-MM-dd)
function getChartDateRange(periodType) {
    const today = new Date();
    switch (periodType) {
        case "hour":
            // 최근 7일을 시간대별로 집계
            return { from: subDays(today, 6), to: today };
        case "dayofweek":
            // 최근 4주를 요일별로 집계
            return { from: startOfWeek(subWeeks(today, 3)), to: endOfWeek(today) };
        case "week":
            // 5주 전 ~ 이번 주
            return { from: startOfWeek(subWeeks(today, 5)), to: endOfWeek(today) };
        case "month":
            // 12개월 전 ~ 이번 달
            return { from: startOfMonth(subMonths(today, 12)), to: endOfMonth(today) };
        case "year":
            // 5년 전 ~ 올해
            return { from: startOfYear(subYears(today, 5)), to: endOfYear(today) };
        case "day":
        default:
            // 최근 7일
            return { from: subDays(today, 6), to: today };
    }
}

// 응답 항목의 X축 라벨 (기간 구분 필드: date/hour/dayofweek)
function toChartLabel(item, periodType) {
    if (periodType === "hour") return `${String(item.hour ?? 0).padStart(2, "0")}:00`;
    if (periodType === "dayofweek") return (item.dayofweek ?? "-").slice(0, 3);
    if (periodType === "day" || periodType === "week") {
        // yyyy-MM-dd → MM-dd (week 는 주 시작일)
        return (item.date ?? "-").slice(5);
    }
    // month: yyyy-MM, year: yyyy
    return item.date ?? "-";
}

// 매장별 수익 조회(periodType=day) 응답 → CustomCalendarGrid data ({ "yyyy-MM-dd": { store, washer, dryer, combo } })
function toCalendarData(list) {
    if (!Array.isArray(list)) return {};
    return list.reduce((acc, item) => {
        if (!item.date) return acc;
        acc[item.date] = {
            store: item.total ?? 0,
            washer: item.washer?.total ?? 0,
            dryer: item.dryer?.total ?? 0,
            combo: item.combo?.total ?? 0,
        };
        return acc;
    }, {});
}

// 매장별 수익 조회 응답 → CustomLineGraphChart data ({ name, Washer, Dryer, Combo })
function toGraphData(list, periodType) {
    if (!Array.isArray(list)) return [];
    return list.map((item) => ({
        name: toChartLabel(item, periodType),
        Washer: item.washer?.total ?? 0,
        Dryer: item.dryer?.total ?? 0,
        Combo: item.combo?.total ?? 0,
    }));
}

function ReportRecentRevenueSubTabContent({ deviceSubTabs, handleSetSubTab, storeId }) {
    const storeService = new StoreService();
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [calendarHeight, setCalendarHeight] = useState(0);
    const [calendarData, setCalendarData] = useState({});
    // { hourly: [...], daily: [...], ... } — 차트 필터 value 기준
    const [graphData, setGraphData] = useState({});

    // Chart: 매장별 수익 조회 (GET /report/revenue/<store-id>) 를 periodType 6종으로 호출
    useEffect(() => {
        if (!storeId) return;
        CHART_PERIODS.forEach(({ value, periodType }) => {
            const { from, to } = getChartDateRange(periodType);
            storeService
                .getStoreRevenueByStore({
                    storeId,
                    periodType,
                    startDate: format(from, "yyyy-MM-dd"),
                    endDate: format(to, "yyyy-MM-dd"),
                })
                .then((result) => {
                    setGraphData((prev) => ({ ...prev, [value]: toGraphData(result, periodType) }));
                })
                .catch((error) => {
                    console.error(`Error fetching store revenue (${periodType}):`, error);
                    setGraphData((prev) => ({ ...prev, [value]: [] }));
                });
        });
    }, [storeId]);

    // Calendar: 선택된 월의 일별 수익을 매장별 수익 조회(periodType=day) 로 채움
    useEffect(() => {
        if (!storeId) return;
        storeService
            .getStoreRevenueByStore({
                storeId,
                periodType: "day",
                startDate: format(startOfMonth(currentMonth), "yyyy-MM-dd"),
                endDate: format(endOfMonth(currentMonth), "yyyy-MM-dd"),
            })
            .then((result) => {
                setCalendarData(toCalendarData(result));
            })
            .catch((error) => {
                console.error("Error fetching store revenue for calendar:", error);
                setCalendarData({});
            });
    }, [storeId, currentMonth]);

    // 1. ref 생성
    const calendarRef = useRef(null);

    // 3. 컴포넌트가 렌더링/업데이트될 때마다 높이를 측정
    useEffect(() => {
        if (calendarRef.current) {
            // 4. ref를 통해 DOM 요소의 높이를 가져와 상태에 저장
            const height = calendarRef.current.offsetHeight;
            setCalendarHeight(height);
            console.log("측정된 컴포넌트 높이:", height);
        }
        // currentMonth나 data가 변경되어 높이가 바뀔 수 있으므로, 의존성 배열에 추가합니다.
    }, [currentMonth]);

    const handlePrevMonth = () => {
        const prevMonth = new Date(currentMonth);
        prevMonth.setMonth(prevMonth.getMonth() - 1);
        setCurrentMonth(prevMonth);
    };

    const handleNextMonth = () => {
        const nextMonth = new Date(currentMonth);
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        setCurrentMonth(nextMonth);
    };

    const [periodFilter, setPeriodFilter] = useState(["allPeriod"]);
    const [showSelectAlert, setShowSelectAlert] = useState(false);

    return (
        <>
            <WebCalendarGrid>
                <div style={{ height: "auto" }}>
                    <Row justifyContent={"space-between"} width={"100%"}>
                        <CustomSubTab tabs={deviceSubTabs} activeTab={"RecentRevenue"} onClick={handleSetSubTab} />
                        <Row>
                            <CustomMonthSelectButton
                                currentMonth={currentMonth}
                                setCurrentMonth={setCurrentMonth}
                                openedCalendarLeftSide={true}
                            />
                            <SizedBox width={"10px"} />
                            <SizedBox
                                width={"36px"}
                                height={"36px"}
                                border={`1px solid ${Theme.color.gray200}`}
                                borderRadius={"10px"}
                                cursor={"pointer"}
                                onClick={handlePrevMonth}
                            >
                                <CustomIcon
                                    iconName={"page_prev"}
                                    size={"18px"}
                                    color={Theme.color.indigo500}
                                    cursor={"pointer"}
                                />
                            </SizedBox>
                            <SizedBox width={"10px"} />
                            <SizedBox
                                width={"36px"}
                                height={"36px"}
                                border={`1px solid ${Theme.color.gray200}`}
                                borderRadius={"10px"}
                                cursor={"pointer"}
                                onClick={handleNextMonth}
                            >
                                <CustomIcon
                                    iconName={"page_next"}
                                    size={"18px"}
                                    color={Theme.color.indigo500}
                                    cursor={"pointer"}
                                />
                            </SizedBox>
                        </Row>
                    </Row>
                    <SizedBox height={"20px"} width={"100%"} />
                    <div ref={calendarRef}>
                        <CustomCalendarGrid currentMonth={currentMonth} data={calendarData} />
                    </div>
                </div>
                <Padding padding={"58px 0"}>
                    <SizedBox
                        border={`1px solid ${Theme.color.gray250}`}
                        borderRadius={"16px"}
                        width={"100%"}
                        height={`${calendarHeight + 5}px`}
                        alignItem={"flex-start"}
                        justifyContent={"flex-start"}
                        padding={"16px"}
                        backgroundColor={"transparent"}
                        overflow={"hidden"}
                    >
                        <Row justifyContent={"space-between"} width={"100%"}>
                            <C1Text fontWeight={"600"}>Chart</C1Text>
                            <CustomMultiFilterButton
                                icon={"settings"}
                                currentValues={periodFilter}
                                setValues={setPeriodFilter}
                                values={StatusValues}
                                multiText={"Status"}
                                openedLeftSide={true}
                                onEmptySelect={() => setShowSelectAlert(true)}
                            />
                        </Row>
                        <SingleScrollView $overflowY={"auto"} $flex={"1"} $minHeight={"0"}>
                            {CHART_PERIODS.filter(
                                ({ value }) => periodFilter.includes("allPeriod") || periodFilter.includes(value)
                            ).map(({ value, titleKey }) => (
                                <SizedBox
                                    key={value}
                                    padding={"16px 0"}
                                    width={"100%"}
                                    height={"auto"}
                                    alignItem={"flex-start"}
                                >
                                    <C3Text fontWeight={"600"}>{Translate(titleKey)}</C3Text>
                                    <SizedBox height={"16px"} />
                                    <CustomLineGraphChart data={graphData[value] ?? []} />
                                </SizedBox>
                            ))}
                        </SingleScrollView>
                    </SizedBox>
                </Padding>
            </WebCalendarGrid>
            <CustomPopup
                isOpen={showSelectAlert}
                message={Translate("common.selectAtLeastOne")}
                onConfirm={() => setShowSelectAlert(false)}
            />
        </>
    );
}

export default ReportRecentRevenueSubTabContent;
